import React, { useState, useEffect } from 'react'
import {
  Save,
  Settings,
  BookOpen,
  Clock,
  DollarSign,
  Star,
  Target,
  Palette,
  Sparkles,
  Check,
  Plus,
  Trash2,
  GraduationCap,
  Brain,
  Zap
} from 'lucide-react'
import LoadingSpinner from '../LoadingSpinner'
import axios from 'axios'

interface StudentPreferences {
  id?: string
  preferredSubjects: string[]
  preferredTimeSlots: {
    day: string
    startTime: string
    endTime: string
  }[]
  preferredCourseType: 'one-to-one' | 'one-to-many' | 'group'
  priceRange: {
    min: number
    max: number
  }
  teacherPreferences: {
    minRating: number
    preferredSpecialties: string[]
    experienceLevel: 'beginner' | 'intermediate' | 'expert'
  }
  learningGoals: string[]
  learningStyle: 'visual' | 'auditory' | 'kinesthetic' | 'mixed'
  language: string
}

interface PreferencesSettingsProps {
  onPreferencesSaved?: () => void
}

const AVAILABLE_SUBJECTS = [
  { name: '数学', icon: '📐', color: 'indigo' },
  { name: '英语', icon: '🌍', color: 'blue' },
  { name: '物理', icon: '⚡', color: 'amber' },
  { name: '化学', icon: '🧪', color: 'purple' },
  { name: '生物', icon: '🧬', color: 'green' },
  { name: '历史', icon: '📜', color: 'orange' },
  { name: '地理', icon: '🌏', color: 'teal' },
  { name: '政治', icon: '⚖️', color: 'rose' },
  { name: '语文', icon: '📖', color: 'red' },
  { name: '编程', icon: '💻', color: 'cyan' },
  { name: '绘画', icon: '🎨', color: 'pink' },
  { name: '音乐', icon: '🎵', color: 'violet' },
  { name: '体育', icon: '⚽', color: 'emerald' },
  { name: '其他', icon: '✨', color: 'slate' }
]

const DAYS_OF_WEEK = [
  { value: 'monday', label: '周一', short: '一' },
  { value: 'tuesday', label: '周二', short: '二' },
  { value: 'wednesday', label: '周三', short: '三' },
  { value: 'thursday', label: '周四', short: '四' },
  { value: 'friday', label: '周五', short: '五' },
  { value: 'saturday', label: '周六', short: '六' },
  { value: 'sunday', label: '周日', short: '日' }
]

const LEARNING_GOALS = [
  { name: '提高考试成绩', icon: '📈', color: 'green' },
  { name: '补习基础知识', icon: '📚', color: 'blue' },
  { name: '培养兴趣爱好', icon: '💡', color: 'amber' },
  { name: '准备升学考试', icon: '🎯', color: 'red' },
  { name: '职业技能提升', icon: '💼', color: 'purple' },
  { name: '语言学习', icon: '🗣️', color: 'cyan' },
  { name: '艺术素养', icon: '🎭', color: 'pink' },
  { name: '体育健身', icon: '🏃', color: 'emerald' },
  { name: '其他', icon: '✨', color: 'slate' }
]

const LEARNING_STYLES = [
  {
    value: 'visual',
    label: '视觉型',
    desc: '喜欢图表、图像',
    icon: '👁️',
    color: 'from-blue-400 to-indigo-500'
  },
  {
    value: 'auditory',
    label: '听觉型',
    desc: '喜欢讲解、讨论',
    icon: '👂',
    color: 'from-purple-400 to-pink-500'
  },
  {
    value: 'kinesthetic',
    label: '动觉型',
    desc: '喜欢实践、操作',
    icon: '✋',
    color: 'from-emerald-400 to-teal-500'
  },
  {
    value: 'mixed',
    label: '混合型',
    desc: '多种方式结合',
    icon: '🎯',
    color: 'from-amber-400 to-orange-500'
  }
]

const COURSE_TYPES = [
  {
    value: 'one-to-one',
    label: '一对一',
    desc: '专注个性化教学',
    icon: '👤',
    color: 'from-indigo-400 to-purple-500'
  },
  {
    value: 'one-to-many',
    label: '小班课',
    desc: '小班制教学',
    icon: '👥',
    color: 'from-emerald-400 to-teal-500'
  },
  {
    value: 'group',
    label: '大班课',
    desc: '团体学习氛围',
    icon: '👨‍👩‍👧‍👦',
    color: 'from-amber-400 to-orange-500'
  }
]

export const PreferencesSettings: React.FC<PreferencesSettingsProps> = ({ onPreferencesSaved }) => {
  const [preferences, setPreferences] = useState<StudentPreferences>({
    preferredSubjects: [],
    preferredTimeSlots: [],
    preferredCourseType: 'one-to-one',
    priceRange: { min: 50, max: 200 },
    teacherPreferences: {
      minRating: 4.0,
      preferredSpecialties: [],
      experienceLevel: 'intermediate'
    },
    learningGoals: [],
    learningStyle: 'mixed',
    language: 'zh'
  })

  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null)
  const [activeSection, setActiveSection] = useState<string | null>(null)

  useEffect(() => {
    fetchPreferences()
  }, [])

  const fetchPreferences = async () => {
    try {
      setLoading(true)
      const response = await axios.get('/api/ai/preferences')
      if (response.data.data) {
        const serverData = response.data.data
        const safeData = {
          ...serverData,
          preferredSubjects: Array.isArray(serverData.preferredSubjects)
            ? serverData.preferredSubjects
            : [],
          preferredTimeSlots: Array.isArray(serverData.preferredTimeSlots)
            ? serverData.preferredTimeSlots
            : [],
          learningGoals: Array.isArray(serverData.learningGoals) ? serverData.learningGoals : [],
          teacherPreferences: {
            ...serverData.teacherPreferences,
            preferredSpecialties: Array.isArray(serverData.teacherPreferences?.preferredSpecialties)
              ? serverData.teacherPreferences.preferredSpecialties
              : []
          }
        }
        setPreferences((prev) => ({ ...prev, ...safeData }))
      }
    } catch (error) {
      console.error('获取偏好设置失败:', error)
      setMessage({ type: 'error', text: '获取设置失败，请刷新页面重试' })
    } finally {
      setLoading(false)
    }
  }

  const savePreferences = async () => {
    try {
      setSaving(true)
      await axios.put('/api/ai/preferences', preferences)
      setMessage({ type: 'success', text: '设置保存成功！即将跳转到智能推荐...' })
      setTimeout(() => {
        setMessage(null)
        if (onPreferencesSaved) {
          onPreferencesSaved()
        }
      }, 1500)
    } catch (error) {
      console.error('保存偏好设置失败:', error)
      setMessage({ type: 'error', text: '保存失败，请重试' })
    } finally {
      setSaving(false)
    }
  }

  const handleSubjectToggle = (subject: string) => {
    setPreferences((prev) => ({
      ...prev,
      preferredSubjects: (prev.preferredSubjects || []).includes(subject)
        ? (prev.preferredSubjects || []).filter((s) => s !== subject)
        : [...(prev.preferredSubjects || []), subject]
    }))
  }

  const handleGoalToggle = (goal: string) => {
    setPreferences((prev) => ({
      ...prev,
      learningGoals: (prev.learningGoals || []).includes(goal)
        ? (prev.learningGoals || []).filter((g) => g !== goal)
        : [...(prev.learningGoals || []), goal]
    }))
  }

  const addTimeSlot = () => {
    setPreferences((prev) => ({
      ...prev,
      preferredTimeSlots: [
        ...prev.preferredTimeSlots,
        { day: 'monday', startTime: '09:00', endTime: '17:00' }
      ]
    }))
  }

  const updateTimeSlot = (index: number, field: string, value: string) => {
    setPreferences((prev) => ({
      ...prev,
      preferredTimeSlots: prev.preferredTimeSlots.map((slot, i) =>
        i === index ? { ...slot, [field]: value } : slot
      )
    }))
  }

  const removeTimeSlot = (index: number) => {
    setPreferences((prev) => ({
      ...prev,
      preferredTimeSlots: prev.preferredTimeSlots.filter((_, i) => i !== index)
    }))
  }

  // Section Header 组件
  const SectionHeader: React.FC<{
    icon: React.ReactNode
    title: string
    subtitle: string
    color: string
  }> = ({ icon, title, subtitle, color }) => (
    <div className="flex items-start gap-4 mb-6">
      <div
        className={`w-12 h-12 rounded-2xl bg-gradient-to-br ${color} flex items-center justify-center text-white shadow-lg`}
      >
        {icon}
      </div>
      <div>
        <h3 className="text-lg font-bold text-slate-900">{title}</h3>
        <p className="text-sm text-slate-500">{subtitle}</p>
      </div>
    </div>
  )

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-20">
        <div className="relative mb-6">
          <div className="w-16 h-16 rounded-full border-4 border-indigo-100 border-t-indigo-500 animate-spin" />
          <Settings className="w-6 h-6 text-indigo-500 absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" />
        </div>
        <p className="text-slate-600 font-medium">加载偏好设置...</p>
      </div>
    )
  }

  return (
    <div className="max-w-4xl mx-auto">
      {/* 头部卡片 */}
      <div className="relative mb-8 rounded-3xl overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-indigo-500 via-purple-500 to-pink-500" />
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxwYXRoIGQ9Ik0zNiAxOGMtNi42MjcgMC0xMiA1LjM3My0xMiAxMnM1LjM3MyAxMiAxMiAxMiAxMi01LjM3MyAxMi0xMi01LjM3My0xMi0xMi0xMnptMCAxOGMtMy4zMTQgMC02LTIuNjg2LTYtNnMyLjY4Ni02IDYtNiA2IDIuNjg2IDYgNi0yLjY4NiA2LTYgNnoiIGZpbGw9IiNmZmYiIGZpbGwtb3BhY2l0eT0iLjEiLz48L2c+PC9zdmc+')] opacity-20" />
        <div className="relative px-8 py-10">
          <div className="flex items-center gap-4 mb-4">
            <div className="w-14 h-14 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center">
              <Sparkles className="w-7 h-7 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-white">学习偏好设置</h1>
              <p className="text-white/80">完善您的偏好，获得更精准的 AI 推荐</p>
            </div>
          </div>
          <div className="flex items-center gap-2 text-white/70 text-sm">
            <Brain className="w-4 h-4" />
            <span>AI 将根据您的偏好为您智能匹配课程和教师</span>
          </div>
        </div>
      </div>

      {/* 消息提示 */}
      {message && (
        <div
          className={`mb-6 px-5 py-4 rounded-2xl flex items-center gap-3 ${
            message.type === 'success'
              ? 'bg-emerald-50 text-emerald-800 border border-emerald-200'
              : 'bg-rose-50 text-rose-800 border border-rose-200'
          }`}
        >
          {message.type === 'success' ? (
            <Check className="w-5 h-5 text-emerald-600" />
          ) : (
            <Zap className="w-5 h-5 text-rose-600" />
          )}
          <span className="font-medium">{message.text}</span>
        </div>
      )}

      <div className="space-y-8">
        {/* 科目偏好 */}
        <div className="bg-white rounded-3xl border border-slate-100 p-8 shadow-sm">
          <SectionHeader
            icon={<BookOpen className="w-6 h-6" />}
            title="偏好科目"
            subtitle="选择您感兴趣的学习科目"
            color="from-indigo-400 to-purple-500"
          />
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
            {AVAILABLE_SUBJECTS.map((subject) => {
              const isSelected = (preferences.preferredSubjects || []).includes(subject.name)
              return (
                <button
                  key={subject.name}
                  onClick={() => handleSubjectToggle(subject.name)}
                  className={`relative flex items-center gap-2 p-3 rounded-xl border-2 transition-all ${
                    isSelected
                      ? 'border-indigo-500 bg-indigo-50 shadow-md shadow-indigo-100'
                      : 'border-slate-100 hover:border-slate-200 hover:bg-slate-50'
                  }`}
                >
                  <span className="text-xl">{subject.icon}</span>
                  <span
                    className={`text-sm font-medium ${isSelected ? 'text-indigo-700' : 'text-slate-700'}`}
                  >
                    {subject.name}
                  </span>
                  {isSelected && (
                    <div className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-indigo-500 rounded-full flex items-center justify-center">
                      <Check className="w-3 h-3 text-white" />
                    </div>
                  )}
                </button>
              )
            })}
          </div>
        </div>

        {/* 时间偏好 */}
        <div className="bg-white rounded-3xl border border-slate-100 p-8 shadow-sm">
          <div className="flex items-center justify-between mb-6">
            <SectionHeader
              icon={<Clock className="w-6 h-6" />}
              title="偏好时间段"
              subtitle="设置您可以上课的时间段"
              color="from-emerald-400 to-teal-500"
            />
            <button
              onClick={addTimeSlot}
              className="flex items-center gap-2 px-4 py-2 bg-emerald-500 text-white rounded-xl text-sm font-medium hover:bg-emerald-600 transition-colors shadow-lg shadow-emerald-200/50"
            >
              <Plus className="w-4 h-4" />
              添加时间
            </button>
          </div>

          {preferences.preferredTimeSlots.length === 0 ? (
            <div className="text-center py-10 text-slate-400">
              <Clock className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>暂未设置时间段，点击上方按钮添加</p>
            </div>
          ) : (
            <div className="space-y-3">
              {preferences.preferredTimeSlots.map((slot, index) => (
                <div
                  key={index}
                  className="flex items-center gap-4 p-4 bg-slate-50 rounded-2xl border border-slate-100"
                >
                  <select
                    value={slot.day}
                    onChange={(e) => updateTimeSlot(index, 'day', e.target.value)}
                    className="px-4 py-2 bg-white border border-slate-200 rounded-xl text-sm font-medium text-slate-700 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                  >
                    {DAYS_OF_WEEK.map((day) => (
                      <option key={day.value} value={day.value}>
                        {day.label}
                      </option>
                    ))}
                  </select>
                  <div className="flex items-center gap-2">
                    <input
                      type="time"
                      value={slot.startTime}
                      onChange={(e) => updateTimeSlot(index, 'startTime', e.target.value)}
                      className="px-4 py-2 bg-white border border-slate-200 rounded-xl text-sm font-medium text-slate-700 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                    />
                    <span className="text-slate-400">至</span>
                    <input
                      type="time"
                      value={slot.endTime}
                      onChange={(e) => updateTimeSlot(index, 'endTime', e.target.value)}
                      className="px-4 py-2 bg-white border border-slate-200 rounded-xl text-sm font-medium text-slate-700 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                    />
                  </div>
                  <button
                    onClick={() => removeTimeSlot(index)}
                    className="p-2 text-slate-400 hover:text-rose-500 hover:bg-rose-50 rounded-xl transition-all ml-auto"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* 课程类型偏好 */}
        <div className="bg-white rounded-3xl border border-slate-100 p-8 shadow-sm">
          <SectionHeader
            icon={<Target className="w-6 h-6" />}
            title="课程类型偏好"
            subtitle="选择您喜欢的授课方式"
            color="from-purple-400 to-pink-500"
          />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {COURSE_TYPES.map((type) => {
              const isSelected = preferences.preferredCourseType === type.value
              return (
                <button
                  key={type.value}
                  onClick={() =>
                    setPreferences((prev) => ({
                      ...prev,
                      preferredCourseType: type.value as any
                    }))
                  }
                  className={`relative p-6 rounded-2xl border-2 transition-all text-left ${
                    isSelected
                      ? 'border-purple-500 bg-purple-50 shadow-lg shadow-purple-100'
                      : 'border-slate-100 hover:border-slate-200 hover:bg-slate-50'
                  }`}
                >
                  <div
                    className={`w-12 h-12 rounded-xl bg-gradient-to-br ${type.color} flex items-center justify-center text-2xl mb-4 shadow-lg`}
                  >
                    {type.icon}
                  </div>
                  <div className={`font-bold text-lg ${isSelected ? 'text-purple-700' : 'text-slate-800'}`}>
                    {type.label}
                  </div>
                  <div className="text-sm text-slate-500 mt-1">{type.desc}</div>
                  {isSelected && (
                    <div className="absolute top-4 right-4 w-6 h-6 bg-purple-500 rounded-full flex items-center justify-center">
                      <Check className="w-4 h-4 text-white" />
                    </div>
                  )}
                </button>
              )
            })}
          </div>
        </div>

        {/* 价格范围 */}
        <div className="bg-white rounded-3xl border border-slate-100 p-8 shadow-sm">
          <SectionHeader
            icon={<DollarSign className="w-6 h-6" />}
            title="价格偏好"
            subtitle="设置您可以接受的课程价格范围（每小时）"
            color="from-amber-400 to-orange-500"
          />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">最低价格</label>
              <div className="relative">
                <span className="absolute left-4 top-1/2 transform -translate-y-1/2 text-amber-500 font-medium">
                  ¥
                </span>
                <input
                  type="number"
                  value={preferences.priceRange.min}
                  onChange={(e) =>
                    setPreferences((prev) => ({
                      ...prev,
                      priceRange: { ...prev.priceRange, min: Number(e.target.value) }
                    }))
                  }
                  className="w-full pl-10 pr-4 py-3 border border-slate-200 rounded-xl text-lg font-medium text-slate-800 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                  min="0"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">最高价格</label>
              <div className="relative">
                <span className="absolute left-4 top-1/2 transform -translate-y-1/2 text-amber-500 font-medium">
                  ¥
                </span>
                <input
                  type="number"
                  value={preferences.priceRange.max}
                  onChange={(e) =>
                    setPreferences((prev) => ({
                      ...prev,
                      priceRange: { ...prev.priceRange, max: Number(e.target.value) }
                    }))
                  }
                  className="w-full pl-10 pr-4 py-3 border border-slate-200 rounded-xl text-lg font-medium text-slate-800 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                  min="0"
                />
              </div>
            </div>
          </div>
          <div className="mt-4 px-4 py-3 bg-amber-50 rounded-xl border border-amber-200">
            <span className="text-amber-800 font-medium">
              当前范围: ¥{preferences.priceRange.min} - ¥{preferences.priceRange.max}/小时
            </span>
          </div>
        </div>

        {/* 教师偏好 */}
        <div className="bg-white rounded-3xl border border-slate-100 p-8 shadow-sm">
          <SectionHeader
            icon={<Star className="w-6 h-6" />}
            title="教师偏好"
            subtitle="设置您对教师的要求"
            color="from-rose-400 to-pink-500"
          />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">最低评分要求</label>
              <select
                value={preferences.teacherPreferences.minRating}
                onChange={(e) =>
                  setPreferences((prev) => ({
                    ...prev,
                    teacherPreferences: {
                      ...prev.teacherPreferences,
                      minRating: Number(e.target.value)
                    }
                  }))
                }
                className="w-full px-4 py-3 border border-slate-200 rounded-xl text-slate-700 font-medium focus:outline-none focus:ring-2 focus:ring-rose-500 focus:border-transparent"
              >
                <option value={3.0}>⭐ 3.0分及以上</option>
                <option value={3.5}>⭐ 3.5分及以上</option>
                <option value={4.0}>⭐⭐ 4.0分及以上</option>
                <option value={4.5}>⭐⭐ 4.5分及以上</option>
                <option value={5.0}>⭐⭐⭐ 5.0分</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">经验水平偏好</label>
              <select
                value={preferences.teacherPreferences.experienceLevel}
                onChange={(e) =>
                  setPreferences((prev) => ({
                    ...prev,
                    teacherPreferences: {
                      ...prev.teacherPreferences,
                      experienceLevel: e.target.value as any
                    }
                  }))
                }
                className="w-full px-4 py-3 border border-slate-200 rounded-xl text-slate-700 font-medium focus:outline-none focus:ring-2 focus:ring-rose-500 focus:border-transparent"
              >
                <option value="beginner">🌱 初级教师</option>
                <option value="intermediate">🌿 中级教师</option>
                <option value="expert">🌳 资深教师</option>
              </select>
            </div>
          </div>
        </div>

        {/* 学习目标 */}
        <div className="bg-white rounded-3xl border border-slate-100 p-8 shadow-sm">
          <SectionHeader
            icon={<Target className="w-6 h-6" />}
            title="学习目标"
            subtitle="选择您的学习目标，帮助我们推荐合适的课程"
            color="from-cyan-400 to-blue-500"
          />
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {LEARNING_GOALS.map((goal) => {
              const isSelected = (preferences.learningGoals || []).includes(goal.name)
              return (
                <button
                  key={goal.name}
                  onClick={() => handleGoalToggle(goal.name)}
                  className={`relative flex items-center gap-2 p-4 rounded-xl border-2 transition-all ${
                    isSelected
                      ? 'border-cyan-500 bg-cyan-50 shadow-md shadow-cyan-100'
                      : 'border-slate-100 hover:border-slate-200 hover:bg-slate-50'
                  }`}
                >
                  <span className="text-xl">{goal.icon}</span>
                  <span
                    className={`text-sm font-medium ${isSelected ? 'text-cyan-700' : 'text-slate-700'}`}
                  >
                    {goal.name}
                  </span>
                  {isSelected && (
                    <div className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-cyan-500 rounded-full flex items-center justify-center">
                      <Check className="w-3 h-3 text-white" />
                    </div>
                  )}
                </button>
              )
            })}
          </div>
        </div>

        {/* 学习风格 */}
        <div className="bg-white rounded-3xl border border-slate-100 p-8 shadow-sm">
          <SectionHeader
            icon={<Palette className="w-6 h-6" />}
            title="学习风格"
            subtitle="了解您的学习风格，提供更适合的教学方式"
            color="from-pink-400 to-rose-500"
          />
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {LEARNING_STYLES.map((style) => {
              const isSelected = preferences.learningStyle === style.value
              return (
                <button
                  key={style.value}
                  onClick={() =>
                    setPreferences((prev) => ({ ...prev, learningStyle: style.value as any }))
                  }
                  className={`relative p-5 rounded-2xl border-2 transition-all text-center ${
                    isSelected
                      ? 'border-pink-500 bg-pink-50 shadow-lg shadow-pink-100'
                      : 'border-slate-100 hover:border-slate-200 hover:bg-slate-50'
                  }`}
                >
                  <div
                    className={`w-14 h-14 mx-auto rounded-xl bg-gradient-to-br ${style.color} flex items-center justify-center text-2xl mb-3 shadow-lg`}
                  >
                    {style.icon}
                  </div>
                  <div className={`font-bold ${isSelected ? 'text-pink-700' : 'text-slate-800'}`}>
                    {style.label}
                  </div>
                  <div className="text-xs text-slate-500 mt-1">{style.desc}</div>
                  {isSelected && (
                    <div className="absolute top-3 right-3 w-5 h-5 bg-pink-500 rounded-full flex items-center justify-center">
                      <Check className="w-3 h-3 text-white" />
                    </div>
                  )}
                </button>
              )
            })}
          </div>
        </div>

        {/* 保存按钮 */}
        <div className="sticky bottom-6 flex justify-end">
          <button
            onClick={savePreferences}
            disabled={saving}
            className="flex items-center gap-3 px-8 py-4 bg-gradient-to-r from-indigo-500 to-purple-600 text-white rounded-2xl font-bold text-lg shadow-xl shadow-indigo-200/50 hover:shadow-2xl hover:shadow-indigo-300/50 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
          >
            {saving ? (
              <>
                <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                <span>保存中...</span>
              </>
            ) : (
              <>
                <Save className="w-5 h-5" />
                <span>保存设置</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  )
}
